// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Group, GroupDetail } from '../../core/models/groups.model';
import { Loadable, IAMUser, Loaded, LoadingStatus } from '@app/core';
import { GroupDetailsActions, GroupDetailsActionTypes } from './group-details.actions';
import { Role } from '../../../core/models/index';


interface GroupFromList {
  data: {
    group: Group;
    users?: any[];
    roles?: any[];
  };
}

export interface GroupDetailsState {
  [groupId: string]: {
    details: GroupFromList | Loadable | Loaded<GroupDetail>;
    groupUserSelections?: GroupUserSelections;
  };
}

export interface GroupUserSelections {
    users?: IAMUser[];
}


export function groupDetailsReducer(state: any = {}, action: GroupDetailsActions): Readonly<GroupDetailsState> {
  switch (action.type) {
    case GroupDetailsActionTypes.SELECT_GROUP:
      return {
        ...state,
        [action.payload.group_id]: {
          ...(state[action.payload.group_id] || {}),
          details: {
            ...(state[action.payload.group_id] || {}).details,
            data: {
              ...(getGroupDetails(action.payload.group_id)(state) || {}),
              group: action.payload
            }
          }
        }
      };

    case GroupDetailsActionTypes.SELECT_USERS_FOR_GROUP:
      return {
        ...state,
        [action.payload.groupId]: {
          ...state[action.payload.groupId],
          groupUserSelections: {
            ...getGroupUserSelections(action.payload.groupId)(state),
            users: action.payload.selectedUsers
          }
        }
      };

    case GroupDetailsActionTypes.GROUP_DETAILS_LOADING:
      return {
        ...state,
        [action.payload]: {
          ...(state[action.payload] || {}),
          details: {
            ...(state[action.payload] || {}).details,
            status: LoadingStatus.LOADING
          }
        }
      };

    case GroupDetailsActionTypes.GROUP_DETAILS_LOAD_COMPLETE:
      return action.payload
        ? {
            ...state,
            [action.payload.group_id]: {
              details: {
                data: {
                  ...getGroupsDetailsData(state, action.payload.group_id),
                  group: action.payload
                },
                status: LoadingStatus.LOAD_COMPLETE
              }
            }
          }
        : state;

    case GroupDetailsActionTypes.GROUP_DETAILS_LOAD_FAILED:
      return {
        ...state,
        [action.payload]: {
          details: {
            ...(state[action.payload] || {}).details,
            status: LoadingStatus.LOAD_FAILED
          }
        }
      };

    case GroupDetailsActionTypes.CLEAR_GROUP_USER_SELECTIONS:
      return {
        ...state,
        [action.payload]: {
          ...(state[action.payload] || {}),
          groupUserSelections: undefined
        }
      };
    case GroupDetailsActionTypes.GROUP_DETAILS_LOAD_COMPLETE_USERS:
      return {
        ...state,
        [action.payload]: {
          details: {
            status: LoadingStatus.LOAD_COMPLETE,
            data: {
              ...getGroupsDetailsData(state, action.payload.group_id),
              users: action.users,
            },
          }
        }
      };
      case GroupDetailsActionTypes.GROUP_DETAILS_USERS_REMOVE_BY_ID: {
        return {
          [action.payload]: {
            details: {
              status: LoadingStatus.LOAD_COMPLETE,
              data: {
                ...(state[action.payload].details.data),
                users: state[action.payload].details.data.users.filter((user: any) => user.user_group_id !== action.userGroupId)
              },
            }
          }
        };
      }
  }

  return state;
}

export function getGroups(state: any, groupId: string) {
  if (!state) {
    return {};
  }

  return state[groupId];
}

export function getGroupsDetails(state: any, groupId: string) {

  if (!state || !state[groupId] || !state[groupId].details) {
    return {};
  }

  return state[groupId].details;
}
export function getGroupsDetailsData(state: any, groupId: string) {

  if (!state || !state[groupId] || !state[groupId].details || !state[groupId].details.data) {
    return {};
  }

  return state[groupId].details.data;
}

export function getGroupDetailsLoadable(groupId: string): (state: GroupDetailsState) => Loadable | undefined {
  return (state: GroupDetailsState) => {
    if (!state[groupId]) {
      return undefined;
    }
    const loadableDetails = <Loadable>state[groupId].details;
    if (!loadableDetails.status) {
      return undefined;
    }
    return loadableDetails;
  };
}

export function getGroupDetails(groupId: string): (state: GroupDetailsState) => GroupDetail | undefined {
  return (state: GroupDetailsState) => {
    if (!state[groupId]) {
      return undefined;
    }
    return state[groupId].details.data;
  };
}

export function getSelectedUsers(groupId: string): (state: GroupDetailsState) => IAMUser[] {
  return (state: GroupDetailsState) => {
    const groupUserSelections = getGroupUserSelections(groupId)(state);
    if (!groupUserSelections || !groupUserSelections.users) {
      return [];
    }
    return groupUserSelections.users;
  };
}



export function getGroupUserSelections(groupId: string): (state: GroupDetailsState) => GroupUserSelections | undefined {
  return (state: GroupDetailsState) => {
    if (!state[groupId]) {
      return undefined;
    }

    return state[groupId].groupUserSelections;
  };
}

export function getUserToEdit(groupId: string): (state: GroupDetailsState) => IAMUser | undefined {
  return (state: GroupDetailsState) => {
    if (!state[groupId]) {
      return undefined;
    }

    const selections = state[groupId].groupUserSelections;
    if (selections === undefined || selections.users === undefined || selections.users.length !== 1) {
      return undefined;
    }
    return selections.users[0];
  };
}



// WEBPACK FOOTER //
// ./src/app/access-management/state/group-details/group-details.reducers.ts